/**
 * The gutter between a panel card and the shell edge, in px — the inset that makes
 * the bento read as floating cards rather than flush panes.
 *
 * Shared because two surfaces render the SAME bento: the workspace and the
 * share-view. It used to be a private `const` inside WorkspaceView with a copy
 * hand-typed into the share-view, which meant retuning one silently desynced the
 * other — the exact drift the shared layout was supposed to eliminate.
 *
 * Lives HERE (not in CSS) because it's applied as a panel MARGIN in JS: the inset
 * has to collapse to 0 with a collapsing panel, so the editor slides out to the
 * shell edge instead of leaving a fixed gap behind.
 */
export const PANEL_INSET = 12

import type { RefObject } from 'react'

export interface PanelResizeConfig {
  /** Element whose inline width is written DIRECTLY during the drag (no React
   *  state in the hot path — setState per frame re-renders every mounted
   *  editor tab and lags the drag). Committed to React only on pointer-up. */
  elRef: RefObject<HTMLElement | null>
  /** Panel width (px) at drag start. */
  startWidth: number
  /** Minimum width before the rubber-band / hard clamp kicks in. */
  min: number
  /** Maximum width (hard clamp). Compute at the call site from the live
   *  container width so it reflects the sibling panel's current size. */
  max: number
  /** +1 if the panel grows as the pointer moves RIGHT (a left-docked panel),
   *  -1 if it grows as the pointer moves LEFT (a right-docked panel). */
  dir: 1 | -1
  /** Commit the final width to React state / persistence. Not called on collapse
   *  or when the width is unchanged. */
  onCommit: (width: number) => void
  /** Runs once at drag start (block iframe pointer events, set the page cursor,
   *  mark the handle active). */
  onStart?: () => void
  /** Runs once at drag end — on commit OR collapse (unblock iframes, clear the
   *  active handle). */
  onEnd?: () => void
  /** Enable drag-past-min rubber-band + snap-collapse. Omit for a plain
   *  min/max clamp (no collapse). */
  collapse?: {
    /** Pull this many px past `min` to snap the panel closed. */
    snapOver: number
    /** Rubber-band give: the asymptotic px of stretch past `min`. */
    stretch: number
    /** Called when the drag snaps the panel closed. */
    onCollapse: () => void
  }
}

/**
 * The one panel-resize drag loop, shared by WorkspaceView's explorer/chat
 * handles and the share-viewer sidebar. Writes width straight to the DOM
 * during the drag and commits to React once on pointer-up; optionally
 * rubber-bands past the minimum and snaps closed. Uses pointer* events so
 * touch + pen drags work, and pointercancel covers the browser reclaiming
 * the gesture mid-drag.
 *
 * Call it from an `onPointerDown` handler: `e => startPanelResize(e, cfg)`.
 */
export function startPanelResize(e: { clientX: number; preventDefault: () => void }, cfg: PanelResizeConfig) {
  e.preventDefault()
  cfg.onStart?.()
  const el = cfg.elRef.current
  if (el) el.style.transition = 'none' // direct, lag-free width writes while dragging
  const startX = e.clientX
  let latest = cfg.startWidth
  let collapsed = false

  const teardown = () => {
    cfg.onEnd?.()
    document.removeEventListener('pointermove', onMove)
    document.removeEventListener('pointerup', onUp)
    document.removeEventListener('pointercancel', onUp)
    if (el) el.style.transition = '' // restore any CSS width transition
  }
  const onMove = (ev: PointerEvent) => {
    const desired = cfg.startWidth + cfg.dir * (ev.clientX - startX)
    if (desired < cfg.min) {
      if (cfg.collapse) {
        const over = cfg.min - desired // px dragged past the min
        if (over >= cfg.collapse.snapOver) {
          collapsed = true
          teardown()
          cfg.collapse.onCollapse()
          return
        }
        // Exponential resistance: slope 1 at the min (seamless), asymptoting
        // to `stretch` px of give.
        latest = cfg.min - cfg.collapse.stretch * (1 - Math.exp(-over / cfg.collapse.stretch))
      } else {
        latest = cfg.min // hard clamp when collapse is disabled
      }
    } else {
      latest = Math.min(cfg.max, desired)
    }
    if (el) el.style.width = `${latest}px`
  }
  const onUp = () => {
    if (collapsed) return
    teardown()
    const finalW = Math.max(cfg.min, latest) // snap back up if rubber-banded
    if (el) el.style.width = `${finalW}px` // CSS transition (if any) animates the snap
    if (finalW !== cfg.startWidth) cfg.onCommit(finalW)
  }

  document.addEventListener('pointermove', onMove)
  document.addEventListener('pointerup', onUp)
  document.addEventListener('pointercancel', onUp)
}
